import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type AxiosRequestConfig,
} from 'axios';
import { useAuthStore } from '../../stores/authStore';
import { handleApiError } from '../utils/errorHandler';
import { showError } from '../utils/notificationHelpers';
import { env } from './env';
import type { LoginCredentials, LoginResponse } from '../../types/auth';
import { normalizeError } from '../utils/errorHandling';
import {
  emitShopifyDisconnected,
  isShopifyDisconnected,
} from '@lib/services/shopify/connectionError';
import { isErrorEnvelope, type ErrorEnvelope } from './errorEnvelope';
import { getDevLane, LANE_HEADER } from './lane';
import { planRetry, RETRY_MAX_ATTEMPTS } from './retryPolicy';

export interface ApiResponse<T = unknown> {
  data: T;
  message?: string;
  success: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// GraphQL types
export interface GraphQLRequest {
  operationName?: string;
  variables?: Record<string, unknown>;
  query: string;
}

export interface GraphQLResponse<T = unknown> {
  data: T;
  errors?: Array<{
    message: string;
    locations?: Array<{ line: number; column: number }>;
    path?: string[];
  }>;
}

export interface RetryConfig {
  maxRetries: number;
  retryStatusCodes: number[];
  retryDelay: number;
}

/**
 * 从响应头里取 `Retry-After`。
 *
 * <p>axios 的 headers 有两副面孔：真实运行时是 `AxiosHeaders` 实例（有 `get()`，大小写不敏感），
 * 而测试里 mock 出来的常常只是个普通对象。两种都读，读不到就交回 null。
 */
function readRetryAfter(headers: unknown): string | null {
  if (headers == null || typeof headers !== 'object') return null;
  const bag = headers as Record<string, unknown> & { get?: (name: string) => unknown };
  if (typeof bag.get === 'function') {
    const viaGetter = bag.get('retry-after');
    if (viaGetter != null) return String(viaGetter);
  }
  const raw = bag['retry-after'] ?? bag['Retry-After'];
  return raw == null ? null : String(raw);
}

class ApiClient {
  private client: AxiosInstance;
  private isRefreshing = false;
  private isLoggingOut = false;
  private refreshPromise: Promise<string> | null = null;
  private failedQueue: Array<{ resolve: (value?: any) => void; reject: (error: Error) => void }> =
    [];
  private pendingRequests = new Map<string, Promise<AxiosResponse>>();
  private isBlocked = false; // 阻止新请求的标志

  /**
   * {@link graphql} 专用的重试配置。
   *
   * <p><b>这不是全站的重试口径</b> —— 全站的在 {@code retryPolicy.ts}，接在响应拦截器上，
   * 覆盖所有方法。这一份只服务 {@link graphql}：拦截器对写请求上的 502 不重试（歧义，
   * 可能已经建好了），而 GraphQL 全走 POST，所以那条老路留着，管它自己那一种情况。
   *
   * <p>两者不会叠加重试：拦截器重试过的码（429 / 503 / 读请求上的 5xx）走到这里时
   * 已经被 resolve 成信封，不是 rejection，{@link retryRequest} 看不到。
   */
  private defaultRetryConfig: RetryConfig = {
    maxRetries: 3,
    retryStatusCodes: [502],
    retryDelay: 1000,
  };

  // OAuth/绑定相关的API白名单 - 这些API错误时不应该触发自动重定向到登录页
  private oauthWhitelist: string[] = [
    '/oauth2/google/login',
    '/oauth2/google/login/callback',
    '/auth/login-google',
    '/auth/external-register',
    // Onboarding v3 "Continue with Shopify" handoff: can be called unauthenticated
    // (no idToken yet) or by an already-signed-in merchant. A genuine 401 here
    // (e.g. a corrupted token) should surface on the handoff page itself, not
    // trigger the global auto-logout redirect — note this whitelist entry does
    // NOT cover a merely-stale idToken: the backend accepts that request but
    // silently answers as if the merchant were signed out (no 401 at all), which
    // ShopifyHandoffPage instead guards against with a proactive refresh before
    // its first call (see that page's pre-POST refresh-or-reset step).
    '/auth/shopify',
  ];

  // 当前正在处理的OAuth请求URL（用于追踪OAuth流程中的请求）
  private currentOAuthRequest: string | null = null;

  // 检查URL是否在OAuth白名单中
  isOAuthWhitelistedEndpoint(url?: string): boolean {
    if (!url) return false;
    return this.oauthWhitelist.some(endpoint => url.includes(endpoint));
  }

  // 设置当前OAuth请求（用于追踪）
  setCurrentOAuthRequest(url: string | null): void {
    this.currentOAuthRequest = url;
  }

  // 检查是否正在处理OAuth请求
  isProcessingOAuthRequest(): boolean {
    return this.currentOAuthRequest !== null;
  }

  // 获取当前OAuth请求URL
  getCurrentOAuthRequest(): string | null {
    return this.currentOAuthRequest;
  }

  constructor() {
    this.client = axios.create({
      baseURL: env.apiBaseUrl ? `${env.apiBaseUrl}/chatbot/api/v1` : '/api',
      timeout: env.apiTimeout || 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  // 动态设置 baseURL 的方法
  setBaseURL(
    type:
      'shopify' | 'chatbot' | 'graphql' | 'messagehub' | 'yepairag' | 'thirdparty' | 'funnelfuse'
  ) {
    // For local development with funnelfuse, use proxy path
    const isLocalDev =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    if (type === 'shopify') {
      this.client.defaults.baseURL = `${env.apiBaseUrl}/shopify/api/service`;
    } else if (type === 'chatbot') {
      this.client.defaults.baseURL = `${env.apiBaseUrl}/chatbot/api/v1`;
    } else if (type === 'graphql') {
      this.client.defaults.baseURL = `${env.apiBaseUrl}/chatbot`;
    } else if (type === 'messagehub') {
      this.client.defaults.baseURL = `${env.apiBaseUrl}/messagehub/api/v1/twilio`;
    } else if (type === 'yepairag') {
      this.client.defaults.baseURL = `${env.apiBaseUrl}/yepairag/api`;
    } else if (type === 'thirdparty') {
      this.client.defaults.baseURL = `${env.apiBaseUrl}/thirdpartyintergration`;
    } else if (type === 'funnelfuse') {
      // Use proxy for local development to avoid CORS
      this.client.defaults.baseURL = isLocalDev
        ? '/dev/funnelfuse/api'
        : `${env.apiBaseUrl}/funnelfuse/api`;
    } else {
      this.client.defaults.baseURL = env.apiBaseUrl ? `${env.apiBaseUrl}/chatbot/api/v1` : '/api';
    }
  }

  private setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      config => {
        // 如果当前被阻止（正在刷新token），则暂停新请求
        if (this.isBlocked && !config.url?.includes('/auth/login-auto')) {
          console.debug('Request blocked due to token refresh in progress:', config.url);
          return new Promise((resolve, reject) => {
            // 将请求加入失败队列，等待token刷新完成
            this.failedQueue.push({
              resolve: () => {
                const { idToken } = useAuthStore.getState();
                if (idToken) {
                  config.headers.Authorization = `Bearer ${idToken}`;
                }
                resolve(config);
              },
              reject: reject,
            });
          });
        }

        const { idToken } = useAuthStore.getState();
        if (idToken) {
          config.headers.Authorization = `Bearer ${idToken}`;
        }
        const lane = getDevLane();
        if (lane) {
          config.headers[LANE_HEADER] = lane;
        }
        return config;
      },
      error => Promise.reject(error)
    );

    // Response interceptor —— 错误信封只有<b>一条处理路径</b>，见 handleErrorEnvelope 的说明。
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        // 后端把业务失败塞在一个 HTTP 200 的 body 里。判定用全仓唯一那个谓词。
        if (!isErrorEnvelope(response.data)) return response;
        const envelope = response.data;

        this.noticeErrorEnvelope(envelope);

        // 登录时的「邮箱未验证」不是失败，是流程里的一步 —— 原样交回给登录处理器。
        if (
          response.config.url?.includes('/auth/login') &&
          envelope.errorCode === 400 &&
          envelope.errorMessage === 'User email unconfirmed'
        ) {
          return response;
        }

        // HTTP 200 + 信封 → reject（口径见 handleErrorEnvelope）。
        const error = this.envelopeAsRejection(envelope, response);
        this.handleGlobalError(error);
        throw error;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config;

        // Normalize error to new AppError structure if not already done.
        //
        // <b>这里过去用的是另一个谓词</b>：`'errorCode' in data && 'subtype' in data`，
        // 也就是「后端有没有给出 subtype」。信封的 subtype 是<b>可选</b>的（全仓七份判定都这么写），
        // 所以一个带 errorCode + errorMessage、没带 subtype 的信封会掉进 else，
        // 从 AxiosError 归一化 —— 后端那句真正的错误信息就丢了。而上面那条分支
        // （HTTP 200 里的信封）一直是无条件用信封归一化的：<b>同一件事，两条分支两个答案</b>。
        //
        // 现在两边都用同一个谓词。`normalizeError` 本身早就同时认得两种形状
        // （有 subtype 走类型化分支，只有 errorCode 走 convertLegacyError），所以不需要在这里再挡一道。
        if (!(error as any).appError) {
          const data = error.response?.data;
          (error as any).appError = normalizeError(isErrorEnvelope(data) ? data : error);
        }

        // <h3>瞬时失败先退避重试</h3>
        //
        // <p>该不该重试、等多久，全在 `retryPolicy.ts`，这里只负责执行。
        //
        // <p><b>为什么放在拦截器里，而不是放在调用方。</b>下面那句 `envelopeAsResolution()`
        // 会把除 401 / 502 之外的所有 HTTP 错误 <b>resolve</b> 成「status 200 + 错误信封」。
        // 也就是说 503、504、429 到调用方手里时已经是一个「成功」的 promise —— 调用方那一层
        // 的任何重试机制都看不到它们。放在这里是唯一还能看见真实失败的位置。
        //
        // <p>重试次数用尽后不做任何特殊处理，直接落回下面原来那套，所以对外契约一行没变。
        // 重试期间也不调 `handleGlobalError` —— 文档要的是「不算断开」，一次会被重试的抖动
        // 不该弹错误提示。
        if (!this.isLoggingOut && originalRequest) {
          const attempted = Number((originalRequest as any)._retryCount ?? 0);
          const plan = planRetry({
            status: error.response?.status,
            method: originalRequest.method,
            code: error.code,
            hasResponse: error.response != null,
            retryAfter: readRetryAfter(error.response?.headers),
            attempt: attempted,
          });
          if (plan) {
            (originalRequest as any)._retryCount = plan.attempt;
            console.warn(
              `[api] ${(originalRequest.method ?? 'get').toUpperCase()} ${originalRequest.url} ` +
                `failed (${error.response?.status ?? error.code ?? 'no response'}); ` +
                `retry ${plan.attempt}/${RETRY_MAX_ATTEMPTS} in ${plan.delayMs}ms` +
                `${plan.honoredRetryAfter ? ' (Retry-After)' : ''}`
            );
            await this.delay(plan.delayMs);
            return this.client.request(originalRequest);
          }
        }

        // For 502 errors, let them be thrown so retry mechanism can handle them
        if (error.response?.status === 502) {
          // Still call handleGlobalError for logging, but with skipNotification
          this.handleGlobalError(error, true);
          // Throw the error so retry mechanism can catch it
          return Promise.reject(error);
        }

        // For all other non-401 errors, return error data instead of throwing
        // This allows the calling code to handle these errors gracefully
        if (error.response?.status !== 401) {
          const data = error.response?.data;
          if (isErrorEnvelope(data)) this.noticeErrorEnvelope(data);

          // Log the error but skip notification
          this.handleGlobalError(error, true);

          // 真实 HTTP 错误 → resolve 成 status:200（口径见 handleErrorEnvelope）。
          return this.envelopeAsResolution(error);
        }

        if (error.response?.status === 401 && originalRequest && !(originalRequest as any)._retry) {
          console.warn('401 Unauthorized response received');

          // Mark this request as retried to prevent infinite loops
          (originalRequest as any)._retry = true;

          // Check if this is a whitelisted OAuth endpoint - skip redirect if so
          const isOAuthRequest = this.isOAuthWhitelistedEndpoint(originalRequest.url);
          if (isOAuthRequest) {
            console.log(
              'OAuth whitelisted endpoint, skipping auto-redirect on 401:',
              originalRequest.url
            );
          }

          // Immediately block new requests when 401 is detected
          this.isBlocked = true;

          // Try to refresh token
          const { user, refreshToken, setTokens, logout } = useAuthStore.getState();

          if (user?.sub && refreshToken) {
            try {
              // If there's already a refresh in progress, wait for it
              if (this.refreshPromise) {
                console.log('Token refresh already in progress, waiting...');
                const newToken = await this.refreshPromise;
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return this.client.request(originalRequest);
              }

              // Start new refresh process
              console.log('Starting new token refresh...');
              this.isRefreshing = true;

              this.refreshPromise = this.performTokenRefresh(user.sub, refreshToken, setTokens);
              const newToken = await this.refreshPromise;

              // Process queued requests with new token
              this.processFailedQueue(newToken, null);

              // Retry the original request with the new token
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.client.request(originalRequest);
            } catch (refreshError) {
              console.error('Token refresh failed:', refreshError);

              // Process queued requests with error
              this.processFailedQueue(null, refreshError as Error);

              // Perform logout only once - skip redirect for OAuth endpoints
              this.performLogout(logout, isOAuthRequest);
              return Promise.reject(refreshError);
            } finally {
              this.isRefreshing = false;
              this.refreshPromise = null;
              this.isBlocked = false; // 恢复请求
              console.log('Token refresh completed, unblocking requests');
            }
          } else {
            console.warn('No valid refresh token or user sub available, logging out');
            // Skip redirect for OAuth endpoints
            this.performLogout(logout, isOAuthRequest);
            this.isBlocked = false; // 确保在登出时也恢复请求
          }
        }
        return Promise.reject(error);
      }
    );
  }

  private processFailedQueue(token: string | null, error: Error | null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else if (token) {
        resolve(token);
      } else {
        // 如果既没有错误也没有token，使用当前的token（用于请求配置的情况）
        resolve();
      }
    });

    this.failedQueue = [];
  }

  /**
   * 换一个新的 idToken，给<b>不经过这个 axios 实例</b>的调用方用。
   *
   * <p>目前只有两处：Listing Hub 的 `listingApi` 与 Product Management 的 `catalogueApi`。
   * 它们用裸 `fetch` 打同一套后端，因此拿不到上面那个 401 拦截器的自动续期 —— idToken 只有
   * 60 分钟，商家在向导或商品表上停留超过一小时再动一下就会撞上它，屏幕上写着
   * 「Your session has expired」，而 refreshToken 其实好好的。
   *
   * <p><b>复用同一个 `refreshPromise`</b>：同时有多个 401 时只真的刷一次，其余等这一次的结果。
   * 否则一页 25 个详情请求同时过期就会打 25 次 login-auto，后端会把后到的那些当成
   * refreshToken 被重放。
   *
   * @returns 新的 idToken（同时已经写回 authStore，调用方重新读一次即可）
   */
  async refreshIdToken(): Promise<string> {
    if (this.refreshPromise) return this.refreshPromise;
    const { user, refreshToken, setTokens } = useAuthStore.getState();
    if (!user?.sub || !refreshToken) throw new Error('No refresh token available');
    this.refreshPromise = this.performTokenRefresh(user.sub, refreshToken, setTokens);
    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async performTokenRefresh(
    userSub: string,
    refreshToken: string,
    setTokens: (tokens: { idToken: string; accessToken: string; refreshToken: string }) => void
  ): Promise<string> {
    console.log('Performing token refresh...');

    // Call login-auto endpoint to refresh token
    const refreshResponse = await axios.post(
      `${env.apiBaseUrl}/chatbot/api/v1/auth/login-auto`,
      {
        sub: userSub,
        token: refreshToken,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 10000,
      }
    );

    if (refreshResponse.data && refreshResponse.data.idToken) {
      console.log('Token refresh successful');

      const newToken = refreshResponse.data.idToken;

      // Update tokens in store
      setTokens({
        idToken: newToken,
        accessToken: refreshResponse.data.accessToken || newToken,
        refreshToken: refreshResponse.data.refreshToken || refreshToken,
      });

      return newToken;
    } else {
      throw new Error('Invalid refresh response');
    }
  }

  private performLogout(logout: () => Promise<void>, skipRedirect: boolean = false) {
    // Add a flag to prevent multiple logout attempts
    if (!this.isLoggingOut) {
      this.isLoggingOut = true;

      console.warn('Scheduling logout...');
      // Use timeout to ensure React render cycle completes
      setTimeout(async () => {
        try {
          console.log('Executing logout...');
          await logout();
          // Force redirect after logout (unless skipRedirect is true)
          if (!skipRedirect) {
            setTimeout(() => {
              window.location.href = '/auth/login';
            }, 100);
          }
        } catch (logoutError) {
          console.error('Error during logout:', logoutError);
          // Force redirect even if logout fails (unless skipRedirect is true)
          if (!skipRedirect) {
            window.location.href = '/auth/login';
          }
        } finally {
          // Reset flag after logout attempt
          setTimeout(() => {
            this.isLoggingOut = false;
          }, 2000);
        }
      }, 1000);
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async retryRequest<T>(
    requestFn: () => Promise<T>,
    config: RetryConfig = this.defaultRetryConfig,
    attempt: number = 0
  ): Promise<T> {
    try {
      return await requestFn();
    } catch (error) {
      const axiosError = error as AxiosError;
      const statusCode = axiosError.response?.status;

      // 如果错误状态码在重试列表中，且还有重试次数
      if (
        statusCode &&
        config.retryStatusCodes.includes(statusCode) &&
        attempt < config.maxRetries
      ) {
        console.log(
          `Request failed with ${statusCode}, retrying... (${attempt + 1}/${config.maxRetries})`
        );

        // 延迟后重试
        await this.delay(config.retryDelay * (attempt + 1)); // 递增延迟
        return this.retryRequest(requestFn, config, attempt + 1);
      }

      // 如果不需要重试或已达到最大重试次数，抛出错误
      throw error;
    }
  }

  /**
   * <h3>错误信封：一条路径，一处口径</h3>
   *
   * <p>后端把业务失败表达成一个 body（<code>{ errorCode, errorMessage, subtype?, data? }</code>），
   * 而这个 body <b>可能挂在 HTTP 200 上，也可能挂在真实的 HTTP 错误码上</b>。
   * 于是拦截器有两个入口：成功处理器与错误处理器。这两个入口在合并之前<b>各自手写了一遍</b>
   * 「怎么判定信封、怎么归一化、怎么交给调用方」，并且已经开始分叉 ——
   * 判定式抄了三份、其中一份问的是 <code>subtype</code> 在不在（见错误处理器里的说明）、
   * 归一化的输入两边不一样。
   *
   * <p>现在两个入口都走同一串动作：
   * <ol>
   *   <li><b>判定</b> —— {@link isErrorEnvelope}，全仓唯一一份（<code>lib/core/errorEnvelope.ts</code>）；</li>
   *   <li><b>知会</b> —— {@link noticeErrorEnvelope}，信封到达时要喊的话都在这一处；</li>
   *   <li><b>归一化</b> —— <code>normalizeError(envelope)</code>，两边同一个输入；</li>
   *   <li><b>交付</b> —— {@link envelopeAsRejection} 或 {@link envelopeAsResolution}。</li>
   * </ol>
   *
   * <h3>唯一还分叉的一步，以及为什么不动它</h3>
   *
   * <p>第 4 步两条分支<b>方向相反</b>，这是<b>历史约定</b>，不是疏漏：
   * <ul>
   *   <li><b>HTTP 200 + 信封 → reject</b>，调用方在 <code>catch</code> 里拿到异常；</li>
   *   <li><b>真实 HTTP 错误 + 信封 → resolve</b>，body 被改写成一个 <code>status: 200</code> 的响应，
   *       调用方要自己 <code>isErrorEnvelope(response.data)</code> 才知道失败了。</li>
   * </ul>
   *
   * <p><b>为什么不把它们也统一。</b>把第二条改成 reject，全仓三十来处
   * <code>isErrorEnvelope(response.data)</code> 判定会同时失效；其中有几处是<b>故意吞掉</b>失败的
   * （<code>return []</code> / <code>return null</code> / <code>return 'failed'</code>），
   * 改成抛异常等于把「这块没数据」变成「这个页面崩了」。更要紧的是：
   * <b>大量调用点根本没有检查信封</b>，今天它们拿到的是一个「成功」响应、渲染出一个空态；
   * 一旦改成 reject，这些地方会变成没人接的 promise rejection。
   * 反过来把第一条改成 resolve 更糟 —— 依赖抛异常的调用方会<b>静默地把失败当成功</b>。
   *
   * <p>所以第 4 步的分叉<b>被收成了一个有名字的选择</b>：两个交付函数、一处说明。
   * 真要翻方向，改的是这两个函数的调用点（各一处），而不是重新读一遍这个拦截器 ——
   * 但那是一次需要连同调用点一起迁移的独立改动，不该夹在一次重构里。
   */
  private noticeErrorEnvelope(envelope: ErrorEnvelope): void {
    // [Infra 1.5 #23] Shopify 连接失效的<b>唯一判定点</b>在 connectionError.ts。
    // 两条分支都必须经过这里：走 resolve 那条路的调用方拿到的是一个「成功」响应，
    // 自己判不出连接断了。
    if (isShopifyDisconnected(envelope)) emitShopifyDisconnected();
  }

  /**
   * 交付方式一：包成一个带 <code>appError</code> 的 AxiosError，由调用处 <code>throw</code>。
   *
   * <p><code>response</code> 只用来取 <code>config</code> 与原始 <code>data</code> ——
   * 保持与合并前完全一致的形状（只有 <code>status</code> 与 <code>data</code> 两个键），
   * 免得下游有人 <code>error.response.headers</code> 拿到一个此前不存在的值。
   */
  private envelopeAsRejection(envelope: ErrorEnvelope, response: AxiosResponse): AxiosError {
    const appError = normalizeError(envelope);
    const error = new AxiosError(appError.message) as any;
    error.response = {
      status: envelope.errorCode || 400,
      data: response.data,
    };
    error.config = response.config;
    error.appError = appError;
    return error as AxiosError;
  }

  /**
   * 交付方式二：包成一个 <code>status: 200</code> 的响应，由调用处 <code>return</code>。
   *
   * <p>后端没给信封时<b>补一个</b>（<code>subtype: 'unknown_error'</code>），这样调用方永远只需要
   * 认一种形状 —— 这一点合并前后没变。
   */
  private envelopeAsResolution(error: AxiosError): AxiosResponse {
    const data = error.response?.data;
    const envelope: ErrorEnvelope = isErrorEnvelope(data)
      ? data
      : {
          errorCode: error.response?.status || 500,
          errorMessage: (data as any)?.message || error.message || 'An error occurred',
          subtype: 'unknown_error',
        };
    return {
      data: envelope,
      status: 200,
      statusText: 'OK',
      headers: error.response?.headers,
      config: error.config as any,
    } as AxiosResponse;
  }

  private handleGlobalError(error: AxiosError, skipNotification: boolean = false) {
    // 502 与所有 skipNotification 的调用只记日志，不弹通知。
    // 真正「会被重试」的判断在 retryPolicy.ts，而那条路径压根不会走到这里 ——
    // 拦截器决定重试时是直接 return 的，不经过本函数。
    const statusCode = error.response?.status;
    if (statusCode === 502 || skipNotification) {
      console.error('API Error (not surfaced to the merchant):', {
        status: statusCode,
        url: error.config?.url,
        data: error.response?.data,
        appError: (error as any).appError,
      });
      return;
    }

    // Use centralized error handler with AppError information
    handleApiError(error, (error as any).appError, undefined);
  }

  // User endpoints
  async getCurrentUser() {
    const response = await this.client.get<ApiResponse>('/users/me');
    return response.data;
  }

  async updateProfile(userData: Record<string, unknown>) {
    const response = await this.client.put<ApiResponse>('/users/me', userData);
    return response.data;
  }

  // Generic CRUD operations - these now ensure chatbot baseURL
  async get<T>(endpoint: string, params?: Record<string, unknown>): Promise<AxiosResponse<T>> {
    // Create a unique key for request deduplication
    const requestKey = `GET:${endpoint}:${JSON.stringify(params || {})}`;

    // If the same request is already pending, return the existing promise
    if (this.pendingRequests.has(requestKey)) {
      console.debug(`Deduplicating GET request: ${requestKey}`);
      return this.pendingRequests.get(requestKey) as Promise<AxiosResponse<T>>;
    }

    // 特殊处理：如果是用户相关的请求且当前正在刷新token，直接阻止
    if (this.isBlocked && (endpoint.includes('/users/') || endpoint.includes('/user'))) {
      console.warn(`Blocking user-related request during token refresh: ${endpoint}`);
      throw new Error('Request blocked during authentication refresh');
    }

    // Create the request promise
    const requestPromise = this.withBaseURL('chatbot', () =>
      this.client.get<T>(endpoint, { params })
    );

    // Store the promise and clean up after completion
    this.pendingRequests.set(requestKey, requestPromise);
    requestPromise.finally(() => {
      this.pendingRequests.delete(requestKey);
    });

    return requestPromise;
  }

  async post<T>(
    endpoint: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.withBaseURL('chatbot', () => this.client.post<T>(endpoint, data, config));
  }

  async put<T>(endpoint: string, data?: unknown): Promise<AxiosResponse<T>> {
    return this.withBaseURL('chatbot', () => this.client.put<T>(endpoint, data));
  }

  async delete<T>(endpoint: string): Promise<AxiosResponse<T>> {
    return this.withBaseURL('chatbot', () => this.client.delete<T>(endpoint));
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<AxiosResponse<T>> {
    return this.withBaseURL('chatbot', () => this.client.patch<T>(endpoint, data));
  }

  // Auth endpoints
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await this.client.post<LoginResponse>('/auth/login', credentials);
    return response.data;
  }

  async logout(): Promise<void> {
    await this.client.post('/auth/logout');
  }

  // GraphQL support with retry mechanism
  async graphql<T = unknown>(
    request: GraphQLRequest,
    endpoint: string = '/graphql',
    retryConfig?: RetryConfig
  ): Promise<GraphQLResponse<T>> {
    const requestFn = async () => {
      const response = await this.withBaseURL('graphql', () =>
        this.client.post<GraphQLResponse<T>>(endpoint, request)
      );

      if (response.data.errors) {
        console.error('GraphQL errors:', response.data.errors);
        throw new Error(response.data.errors.map(e => e.message).join(', '));
      }

      return response.data;
    };

    try {
      return await this.retryRequest(requestFn, retryConfig);
    } catch (error) {
      console.error('GraphQL request failed after retries:', error);

      // 如果重试后仍然是502错误，显示用户友好的错误消息
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 502) {
        showError('Service temporarily unavailable. Please try again later.');
      }

      throw error;
    }
  }

  async graphqlQuery<T = unknown>(
    query: string,
    variables?: Record<string, unknown>,
    operationName?: string,
    endpoint: string = '/graphql',
    retryConfig?: RetryConfig
  ): Promise<GraphQLResponse<T>> {
    const request: GraphQLRequest = {
      query,
      variables,
      operationName,
    };

    return this.graphql<T>(request, endpoint, retryConfig);
  }

  // Auto baseURL switching wrapper methods
  private async withBaseURL<T>(
    baseUrlType:
      'shopify' | 'chatbot' | 'graphql' | 'messagehub' | 'yepairag' | 'thirdparty' | 'funnelfuse',
    operation: () => Promise<T>
  ): Promise<T> {
    const originalBaseURL = this.client.defaults.baseURL;
    this.setBaseURL(baseUrlType);

    // Debug logging in development
    if (import.meta.env.DEV) {
      console.debug(
        `🔄 API baseURL changed: ${originalBaseURL} → ${this.client.defaults.baseURL} (${baseUrlType})`
      );
    }

    try {
      return await operation();
    } finally {
      this.client.defaults.baseURL = originalBaseURL;
      if (import.meta.env.DEV) {
        console.debug(
          `🔄 API baseURL restored: ${this.client.defaults.baseURL} (from ${baseUrlType})`
        );
      }
    }
  }

  // Shopify API support with automatic baseURL switching
  async shopifyGet<T>(
    endpoint: string,
    params?: Record<string, unknown>
  ): Promise<AxiosResponse<T>> {
    return this.withBaseURL('shopify', () => this.client.get<T>(endpoint, { params }));
  }

  async shopifyPost<T>(
    endpoint: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.withBaseURL('shopify', () => this.client.post<T>(endpoint, data, config));
  }

  // Chatbot API support with automatic baseURL switching
  async chatbotGet<T>(
    endpoint: string,
    params?: Record<string, unknown>,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    // Create a unique key for request deduplication
    const requestKey = `CHATBOT_GET:${endpoint}:${JSON.stringify(params || {})}:${JSON.stringify(config?.headers || {})}`;

    // If the same request is already pending, return the existing promise
    if (this.pendingRequests.has(requestKey)) {
      console.debug(`Deduplicating chatbot GET request: ${requestKey}`);
      return this.pendingRequests.get(requestKey) as Promise<AxiosResponse<T>>;
    }

    // 特殊处理：如果是用户相关的请求且当前正在刷新token，直接阻止
    if (this.isBlocked && (endpoint.includes('/users/') || endpoint.includes('/user'))) {
      console.warn(`Blocking user-related chatbot request during token refresh: ${endpoint}`);
      throw new Error('Request blocked during authentication refresh');
    }

    // Create the request promise
    const requestPromise = this.withBaseURL('chatbot', () =>
      this.client.get<T>(endpoint, { params, ...config })
    );

    // Store the promise and clean up after completion
    this.pendingRequests.set(requestKey, requestPromise);
    requestPromise.finally(() => {
      this.pendingRequests.delete(requestKey);
    });

    return requestPromise;
  }

  async chatbotPost<T>(
    endpoint: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.withBaseURL('chatbot', () => this.client.post<T>(endpoint, data, config));
  }

  async chatbotPut<T>(endpoint: string, data?: unknown): Promise<AxiosResponse<T>> {
    return this.withBaseURL('chatbot', () => this.client.put<T>(endpoint, data));
  }

  async chatbotPatch<T>(endpoint: string, data?: unknown): Promise<AxiosResponse<T>> {
    return this.withBaseURL('chatbot', () => this.client.patch<T>(endpoint, data));
  }

  async chatbotDelete<T>(endpoint: string): Promise<AxiosResponse<T>> {
    return this.withBaseURL('chatbot', () => this.client.delete<T>(endpoint));
  }

  async messagehubPost<T>(
    endpoint: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.withBaseURL('messagehub', () => this.client.post<T>(endpoint, data, config));
  }

  async yepairagPost<T>(
    endpoint: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.withBaseURL('yepairag', () => this.client.post<T>(endpoint, data, config));
  }

  async yepairagGet<T>(
    endpoint: string,
    params?: Record<string, unknown>,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.withBaseURL('yepairag', () => this.client.get<T>(endpoint, { params, ...config }));
  }
  async thirdpartyGet<T>(
    endpoint: string,
    params?: Record<string, unknown>
  ): Promise<AxiosResponse<T>> {
    return this.withBaseURL('thirdparty', () => this.client.get<T>(endpoint, { params }));
  }

  async thirdpartyPost<T>(
    endpoint: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.withBaseURL('thirdparty', () => this.client.post<T>(endpoint, data, config));
  }

  async thirdpartyPut<T>(endpoint: string, data?: unknown): Promise<AxiosResponse<T>> {
    return this.withBaseURL('thirdparty', () => this.client.put<T>(endpoint, data));
  }

  async thirdpartyDelete<T>(endpoint: string): Promise<AxiosResponse<T>> {
    return this.withBaseURL('thirdparty', () => this.client.delete<T>(endpoint));
  }

  async funnelfuseGet<T>(
    endpoint: string,
    params?: Record<string, unknown>
  ): Promise<AxiosResponse<T>> {
    return this.withBaseURL('funnelfuse', () => this.client.get<T>(endpoint, { params }));
  }

  async funnelfusePost<T>(
    endpoint: string,
    data?: unknown,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.withBaseURL('funnelfuse', () => this.client.post<T>(endpoint, data, config));
  }

  async funnelfusePut<T>(endpoint: string, data?: unknown): Promise<AxiosResponse<T>> {
    return this.withBaseURL('funnelfuse', () => this.client.put<T>(endpoint, data));
  }

  async funnelfuseDelete<T>(endpoint: string): Promise<AxiosResponse<T>> {
    return this.withBaseURL('funnelfuse', () => this.client.delete<T>(endpoint));
  }
}

export const apiClient = new ApiClient();
